/**
 * Renders a study artifact (plan učenja / flash kartice / kviz / mapa uma)
 * inside the admin transcript.
 *
 * These turns are sent to the student as an empty message plus a CustomElement,
 * so the transcript used to show an italic "No content" placeholder and an admin
 * had no way to see what the student was actually given. The payload here is the
 * element's props verbatim, so the field names match the student-facing elements
 * in `backend/services/public/elements/`.
 */

import type { ReactNode } from 'react';

interface PlanOverview {
  cilj?: string;
  vreme?: string;
  dnevno?: string;
}

interface PlanRow {
  dan?: string;
  trajanje?: string;
  tema?: string;
  aktivnosti?: string;
}

interface PlanGoalBlock {
  naslov?: string;
  stavke?: string[];
}

interface StudyPlanPayload {
  pregled?: PlanOverview;
  raspored?: PlanRow[];
  ciljevi?: PlanGoalBlock[];
  saveti?: string[];
}

interface Flashcard {
  pitanje?: string;
  odgovor?: string;
}

interface QuizQuestion {
  pitanje?: string;
  odgovori?: string[];
  tacan?: number;
}

interface MindMapNode {
  label?: string;
  children?: MindMapNode[];
}

export type StudyArtifactKind = 'plan' | 'kartice' | 'kviz' | 'mapa';

export interface StudyArtifact {
  kind: StudyArtifactKind;
  summary: string;
  payload: {
    plan?: StudyPlanPayload;
    cards?: Flashcard[];
    questions?: QuizQuestion[];
    naslov?: string;
    tree?: MindMapNode;
    koren?: MindMapNode;
    language?: string;
  };
}

const QUIZ_LETTERS = ['A', 'B', 'C', 'D'];

const asArray = <T,>(value: T[] | undefined): T[] =>
  Array.isArray(value) ? value : [];

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mt-3 mb-1.5">
      {children}
    </div>
  );
}

function PlanView({ plan }: { plan: StudyPlanPayload }) {
  const overview = plan.pregled && typeof plan.pregled === 'object' ? plan.pregled : {};
  const schedule = asArray(plan.raspored);
  const goals = asArray(plan.ciljevi);
  const tips = asArray(plan.saveti);

  const overviewRows: [string, string][] = [
    overview.cilj ? ['Cilj', overview.cilj] : null,
    overview.vreme ? ['Vreme', overview.vreme] : null,
    overview.dnevno ? ['Dnevno', overview.dnevno] : null
  ].filter((row): row is [string, string] => row !== null);

  return (
    <div>
      {overviewRows.length > 0 && (
        <div className="space-y-1">
          {overviewRows.map(([label, value]) => (
            <div key={label} className="flex gap-2 text-sm">
              <span className="font-medium text-muted-foreground min-w-16">{label}</span>
              <span className="break-words">{value}</span>
            </div>
          ))}
        </div>
      )}

      {schedule.length > 0 && (
        <>
          <SectionTitle>Raspored</SectionTitle>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="text-left text-xs text-muted-foreground">
                  <th className="py-1 pr-3 font-medium">Dan</th>
                  <th className="py-1 pr-3 font-medium">Tema</th>
                  <th className="py-1 pr-3 font-medium">Aktivnosti</th>
                  <th className="py-1 font-medium">Trajanje</th>
                </tr>
              </thead>
              <tbody>
                {schedule.map((row, idx) => (
                  <tr key={idx} className="border-t border-border/60 align-top">
                    <td className="py-1.5 pr-3 font-medium whitespace-nowrap">
                      {row.dan || `Dan ${idx + 1}`}
                    </td>
                    <td className="py-1.5 pr-3">{row.tema || '-'}</td>
                    <td className="py-1.5 pr-3 text-muted-foreground">
                      {row.aktivnosti || ''}
                    </td>
                    <td className="py-1.5 whitespace-nowrap text-muted-foreground">
                      {row.trajanje || ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {goals.length > 0 && (
        <>
          <SectionTitle>Dnevni ciljevi</SectionTitle>
          <div className="space-y-2">
            {goals.map((block, idx) => (
              <div key={idx}>
                <div className="text-sm font-semibold">
                  {block.naslov || `Blok ${idx + 1}`}
                </div>
                <ul className="list-disc pl-5 text-sm text-muted-foreground">
                  {asArray(block.stavke).map((item, itemIdx) => (
                    <li key={itemIdx}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </>
      )}

      {tips.length > 0 && (
        <>
          <SectionTitle>Saveti</SectionTitle>
          <ul className="list-disc pl-5 text-sm text-muted-foreground">
            {tips.map((tip, idx) => (
              <li key={idx}>{tip}</li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function FlashcardsView({ cards }: { cards: Flashcard[] }) {
  return (
    <ol className="space-y-2.5">
      {cards.map((card, idx) => (
        <li key={idx} className="text-sm">
          <div className="font-medium">
            {idx + 1}. {card.pitanje || '-'}
          </div>
          <div className="pl-5 text-muted-foreground break-words">
            {card.odgovor || ''}
          </div>
        </li>
      ))}
    </ol>
  );
}

function QuizView({ questions }: { questions: QuizQuestion[] }) {
  return (
    <ol className="space-y-3">
      {questions.map((question, idx) => {
        const options = asArray(question.odgovori);
        const correct = typeof question.tacan === 'number' ? question.tacan : -1;
        return (
          <li key={idx} className="text-sm">
            <div className="font-medium">
              {idx + 1}. {question.pitanje || '-'}
            </div>
            <ul className="pl-5 mt-1 space-y-0.5">
              {options.map((option, optionIdx) => {
                const isCorrect = optionIdx === correct;
                return (
                  <li
                    key={optionIdx}
                    className={
                      isCorrect
                        ? 'text-green-700 dark:text-green-400 font-medium'
                        : 'text-muted-foreground'
                    }
                  >
                    {QUIZ_LETTERS[optionIdx] || optionIdx + 1}. {option}
                    {isCorrect && ' ✓'}
                  </li>
                );
              })}
            </ul>
          </li>
        );
      })}
    </ol>
  );
}

function MindMapNodeView({
  node,
  depth = 0
}: {
  node: MindMapNode;
  depth?: number;
}) {
  const children = asArray(node.children);
  const label =
    typeof node.label === 'string' && node.label.trim() ? node.label.trim() : '-';
  return (
    <li className="text-sm">
      <div className={depth === 0 ? 'font-semibold' : 'font-medium'}>{label}</div>
      {children.length > 0 && (
        <ul className="pl-5 mt-1 space-y-1 border-l border-border/50">
          {children.map((child, idx) => (
            <MindMapNodeView key={idx} node={child} depth={depth + 1} />
          ))}
        </ul>
      )}
    </li>
  );
}

function MindMapView({
  naslov,
  tree
}: {
  naslov?: string;
  tree: MindMapNode;
}) {
  return (
    <div>
      {naslov ? (
        <div className="text-sm font-semibold mb-2 break-words">{naslov}</div>
      ) : null}
      <ul className="space-y-1">
        <MindMapNodeView node={tree} />
      </ul>
    </div>
  );
}

export function StudyArtifactView({ artifact }: { artifact: StudyArtifact }) {
  const { kind, summary, payload } = artifact;

  const body = (() => {
    if (kind === 'plan') {
      const plan = payload.plan && typeof payload.plan === 'object' ? payload.plan : {};
      return <PlanView plan={plan} />;
    }
    if (kind === 'kartice') {
      const cards = asArray(payload.cards);
      return cards.length > 0 ? <FlashcardsView cards={cards} /> : null;
    }
    if (kind === 'kviz') {
      const questions = asArray(payload.questions);
      return questions.length > 0 ? <QuizView questions={questions} /> : null;
    }
    if (kind === 'mapa') {
      const tree =
        (payload.tree && typeof payload.tree === 'object' ? payload.tree : null) ||
        (payload.koren && typeof payload.koren === 'object' ? payload.koren : null);
      return tree ? (
        <MindMapView
          naslov={typeof payload.naslov === 'string' ? payload.naslov : undefined}
          tree={tree}
        />
      ) : null;
    }
    return null;
  })();

  return (
    <details open className="rounded border border-border bg-black/5 dark:bg-white/5 p-3">
      <summary className="cursor-pointer select-none text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {summary}
      </summary>
      <div className="mt-2">
        {body || (
          <div className="text-sm text-muted-foreground italic">
            Artifakt je prazan ili u neočekivanom formatu.
          </div>
        )}
        <details className="mt-3">
          <summary className="fongpt-hover-text cursor-pointer select-none text-xs text-muted-foreground">
            Raw JSON
          </summary>
          <pre className="mt-1.5 text-xs overflow-x-auto">
            {JSON.stringify(payload, null, 2)}
          </pre>
        </details>
      </div>
    </details>
  );
}

export default StudyArtifactView;
